import Hyperlink from '@ingka/hyperlink';
import ListView, { ListViewItem } from '@ingka/list-view';
import Text from '@ingka/text';

import type { CustomerInformationCardProps } from '../../types/customer-information.types';
import { isBusinessWithIbn, isFamilyMember } from '../../utils/customer-type.utils';

import './styles.scss';

/**
 * Displays customer's information in a card layout.
 * @param {CardCustomerData} customerData
 * @param {CardTranslations} translations
 * @param {CardModalConfig} modalConfig
 */
export const CustomerInformationCard = ({ customerData, translations, modalConfig }: CustomerInformationCardProps) => {
    const { businessName, email, familyNumber, address, firstName, isBusinessCustomer, lastName, mobile, isIbn } =
        customerData ?? {};

    const isCustomerInformationPresent = [firstName, lastName, email, mobile, address, businessName].some(Boolean);

    if (!isCustomerInformationPresent) {
        return null;
    }

    const { businessNetworkLabel, familyMemberLabel } = translations ?? {};

    const { canChange, onChange, onClick, prefix } = modalConfig ?? {};

    const prefixProp = prefix ? { prefix } : {};

    const fullName = firstName && lastName ? `${firstName} ${lastName}` : (firstName ?? lastName ?? '');

    // If isIbn flag is provided (drafts & quotes), use it.
    // If not (orders) determine if the customer is IBN based on family number and business status.
    const isIbnBusinessCustomer = isIbn ?? isBusinessWithIbn(isBusinessCustomer, familyNumber);
    const isCustomerFamilyMember = isFamilyMember(familyNumber, isBusinessCustomer);
    const isLabelRequired = isCustomerFamilyMember || isIbnBusinessCustomer;

    const customerLabelText = isLabelRequired
        ? isIbnBusinessCustomer
            ? businessNetworkLabel
            : familyMemberLabel
        : undefined;

    const headingContent = businessName ?? fullName;

    const renderDescription = () => (
        <div className="ot-customer-information-card-description">
            {businessName && fullName && (
                <Text {...prefixProp} tagName="p" textStyle="Body.M">
                    {fullName}
                </Text>
            )}
            {email && (
                <Text {...prefixProp} tagName="p" textStyle="Body.M">
                    {email}
                </Text>
            )}
            {mobile && (
                <Text {...prefixProp} tagName="p" textStyle="Body.M">
                    {mobile}
                </Text>
            )}
            {address && (
                <Text {...prefixProp} tagName="p" textStyle="Body.M">
                    {address}
                </Text>
            )}
            {customerLabelText && <div className="ot-customer-information-card-label">{customerLabelText}</div>}
        </div>
    );

    return (
        <div className="ot-customer-information-card">
            <div className="ot-customer-information-card-header">
                <div>
                    {headingContent && (
                        <Text
                            {...prefixProp}
                            tagName="h3"
                            textStyle="Heading.S"
                            className="ot-customer-information-card-heading"
                        >
                            {headingContent}
                        </Text>
                    )}
                </div>
                {canChange && (
                    <Hyperlink
                        {...prefixProp}
                        button
                        text={translations?.changeLabel ?? 'Change'}
                        onClick={() => {
                            onChange?.();
                        }}
                    />
                )}
            </div>
            <ListView {...prefixProp} id="ot-customer-information-card-list" showDivider={false}>
                <ListViewItem
                    {...prefixProp}
                    control="navigational"
                    controlIcon="chevronRight"
                    description={renderDescription()}
                    onClick={() => onClick?.()}
                />
            </ListView>
        </div>
    );
};
